import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import {
  Bug,
  Check,
  X,
} from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import ProcessConfigSkeleton from '../ConfigStatusView2Skeleton';
import '../ConfigStatusView2.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../users-customers/components/UsersCustomersView.css';
import '../../equipe2/components/Equipe2View.css';
import '../../go-config/components/GoDispositivosConfig.css';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import './DefeitosVerveTableV2.css';
import './DefeitosVerveRichTextModal.css';
import { RichTextModal } from './DefeitosVerveRichTextModal';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import './CondicoesIntervencaoConfig.css';
import { DefeitosEquipCompatModal } from './DefeitosEquipCompatModal';
import { DefeitosVerveTableV2 } from './DefeitosVerveTableV2';
import { DefeitosVerveToolbar } from './DefeitosVerveToolbar';
import { DefeitosVerveNativeServiceModal } from './DefeitosVerveNativeServiceModal';
import { getDefeitoSortValue, fullDefeitosVisibilityState } from './defeitosVerveShared';
import {
  parseTags,
  joinTags,
  normSev,
  normOsFlag,
  TIPO_OS_FIELDS,
} from './defeitosVerveTableUi';

const API_BASE_URL = getApiBaseUrl();

function Pcs2Switch({ checked, onChange, ariaLabel, disabled }) {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };
  return (
    <label className="pcs2-switch">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

function Pcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2Subblock({ title, desc, hideHead, action, children }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc || action) ? (
        <div className={`pcs2-subhead${action ? ' pcs2-subhead--with-action' : ''}`}>
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
          {action ? <div className="pcs2-subhead-action">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children, className = '' }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

export default function DefeitosVerveConfig() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enablingId, setEnablingId] = useState(null);
  const [disablingId, setDisablingId] = useState(null);
  const [desabilitarOpenId, setDesabilitarOpenId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draftBoard, setDraftBoard] = useState('');
  const [draftColor, setDraftColor] = useState('');
  const [draftTags, setDraftTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(null);
  const [modalRow, setModalRow] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [sevOpenCod, setSevOpenCod] = useState(null);
  const [tipoOsOpenCod, setTipoOsOpenCod] = useState(null);
  const [equipModalRow, setEquipModalRow] = useState(null);
  const [equipAssocEnabled, setEquipAssocEnabled] = useState(false);
  const [equipAssocLoaded, setEquipAssocLoaded] = useState(false);
  const [equipAssocSaving, setEquipAssocSaving] = useState(false);
  const [colorPaletteOpen, setColorPaletteOpen] = useState(false);
  const [colorPaletteOpenCod, setColorPaletteOpenCod] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('nome');
  const [sortDir, setSortDir] = useState('asc');
  const [colDefeitoVisibility, setColDefeitoVisibility] = useState(fullDefeitosVisibilityState);
  const [draftSev, setDraftSev] = useState('leve');
  const [draftTipoFlags, setDraftTipoFlags] = useState({});
  const [nativeMode, setNativeMode] = useState(false);
  const [statusTab, setStatusTab] = useState('ativos');
  const [serviceForm, setServiceForm] = useState(null);
  const colorEditAnchorRef = useRef(null);
  const colorSaveTimerRef = useRef(null);

  const isDefeitoColVis = useCallback((key) => colDefeitoVisibility[key] !== false, [colDefeitoVisibility]);

  const closeColorPalette = useCallback(() => setColorPaletteOpen(false), []);

  const closeColorPalettePicker = useCallback(() => setColorPaletteOpenCod(null), []);

  const normalizeDraftColor = useCallback((value) => {
    const c = String(value ?? '').trim();
    if (!c) return '';
    if (c.startsWith('#')) return c.slice(0, 7).toUpperCase();
    return `#${c.replace(/^#/, '')}`.slice(0, 7).toUpperCase();
  }, []);

  const openColorPalette = useCallback(
    (r) => {
      setEditing(null);
      setColorPaletteOpen(false);
      setTipoOsOpenCod(null);
      setColorPaletteOpenCod(String(r.TB01048_CODIGO).trim());
      setDraftColor(normalizeDraftColor(r.color_bg));
    },
    [normalizeDraftColor]
  );

  const loadEquipAssocFlag = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve/equip-os-assoc-flag`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const j = await res.json().catch(() => ({}));
      setEquipAssocEnabled(!!j.enabled);
    } catch {
      setEquipAssocEnabled(false);
    } finally {
      setEquipAssocLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadEquipAssocFlag();
  }, [loadEquipAssocFlag]);

  const onEquipAssocToggle = async (checked) => {
    const prev = equipAssocEnabled;
    setEquipAssocEnabled(checked);
    setEquipAssocSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve/equip-os-assoc-flag`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: checked }),
      });
      const j = await res.json().catch(() => ({}));
      if (!j.success) {
        setEquipAssocEnabled(prev);
        setFeedback({ type: 'err', cod: '__equip_assoc__', msg: j.message || 'Erro ao salvar' });
        setTimeout(() => setFeedback(null), 4000);
      }
    } catch {
      setEquipAssocEnabled(prev);
      setFeedback({ type: 'err', cod: '__equip_assoc__', msg: 'Erro de conexão' });
      setTimeout(() => setFeedback(null), 4000);
    } finally {
      setEquipAssocSaving(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${API_BASE_URL}/api/config/defeitos-verve/native/status`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((j) => setNativeMode(!!j.native))
      .catch(() => setNativeMode(false));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const qs = `?includeNative=1${nativeMode ? '&includeInactive=1' : ''}`;
      const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve${qs}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const j = await res.json();
      setRows(Array.isArray(j.data) ? j.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [nativeMode]);

  useEffect(() => {
    load();
  }, [load]);

  const openEditService = useCallback((cod) => setServiceForm({ mode: 'edit', codigo: cod }), []);

  useEffect(() => {
    if (!editing || editing.field !== 'color') setColorPaletteOpen(false);
  }, [editing]);

  const rowEnabled = (r) => r.habilitado === 1 || r.habilitado === true;

  const patchRowLocal = (cod, partial) => {
    setRows((prev) => prev.map((x) => (String(x.TB01048_CODIGO).trim() === cod ? { ...x, ...partial } : x)));
  };

  const apiPatch = async (codDefeito, body) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve/campos`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ codDefeito, ...body }),
    });
    return res.json();
  };

  const saveColorPick = useCallback(
    (row, hex) => {
      const cod = String(row.TB01048_CODIGO).trim();
      const colorBg = normalizeDraftColor(hex);
      setDraftColor(colorBg);
      patchRowLocal(cod, { color_bg: colorBg || null });

      if (colorSaveTimerRef.current) clearTimeout(colorSaveTimerRef.current);
      colorSaveTimerRef.current = setTimeout(async () => {
        setSaving({ cod, field: 'color' });
        try {
          const j = await apiPatch(cod, { colorBg });
          if (!j.success) {
            setFeedback({ type: 'err', cod, msg: j.message || 'Erro ao salvar' });
            load();
            setTimeout(() => setFeedback(null), 4000);
          }
        } catch {
          setFeedback({ type: 'err', cod, msg: 'Erro de conexão' });
          load();
          setTimeout(() => setFeedback(null), 4000);
        } finally {
          setSaving(null);
        }
      }, 350);
    },
    [load, normalizeDraftColor]
  );

  const habilitar = async (cod) => {
    setEnablingId(cod);
    setFeedback(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve/habilitar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ codDefeito: cod }),
      });
      const j = await res.json().catch(() => ({}));
      if (j.success) {
        patchRowLocal(cod, {
          habilitado: 1,
          cod_alert: '',
          color_bg: null,
          classification: '',
          auto_solution_suggestions: '',
          severity_level: 'leve',
          allow_corrective: 1,
          allow_install: 1,
          allow_desinstall: 1,
          allow_preventive: 1,
          allow_stock: 1,
          allow_recharge: 1,
          allow_measurement: 1,
          allow_counter: 1,
          equip_count: 0,
        });
      } else {
        if (res.status === 409) load();
        setFeedback({ type: 'err', cod, msg: j.message || 'Erro' });
        setTimeout(() => setFeedback(null), 3500);
      }
    } catch {
      setFeedback({ type: 'err', cod, msg: 'Erro de conexão' });
      setTimeout(() => setFeedback(null), 3500);
    } finally {
      setEnablingId(null);
    }
  };

  const desabilitar = async (cod) => {
    setDisablingId(cod);
    setFeedback(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve/desabilitar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ codDefeito: cod }),
      });
      const j = await res.json();
      if (j.success) {
        patchRowLocal(cod, {
          habilitado: 0,
          cod_alert: null,
          color_bg: null,
          classification: null,
          auto_solution_suggestions: null,
          severity_level: null,
          allow_corrective: null,
          allow_install: null,
          allow_desinstall: null,
          allow_preventive: null,
          allow_stock: null,
          allow_recharge: null,
          allow_measurement: null,
          allow_counter: null,
          equip_count: 0,
        });
        setDesabilitarOpenId(null);
      } else {
        setFeedback({ type: 'err', cod, msg: j.message || 'Erro' });
        setTimeout(() => setFeedback(null), 3500);
      }
    } catch {
      setFeedback({ type: 'err', cod, msg: 'Erro de conexão' });
      setTimeout(() => setFeedback(null), 3500);
    } finally {
      setDisablingId(null);
    }
  };

  const startEdit = (r, field) => {
    setColorPaletteOpenCod(null);
    const cod = String(r.TB01048_CODIGO).trim();
    if (field === 'board') {
      setEditing({ cod, field: 'board' });
      setDraftBoard(String(r.cod_alert ?? '').trim().slice(0, 4).toUpperCase());
    } else if (field === 'color') {
      setEditing({ cod, field: 'color' });
      setColorPaletteOpen(false);
      const c = String(r.color_bg ?? '').trim();
      setDraftColor(c && c.startsWith('#') ? c.slice(0, 7).toUpperCase() : c ? `#${c.replace(/^#/, '')}`.slice(0, 7) : '');
    } else if (field === 'classif') {
      setEditing({ cod, field: 'classif' });
      setDraftTags(parseTags(r.classification));
      setTagInput('');
    } else if (field === 'sev') {
      setEditing({ cod, field: 'sev' });
      setDraftSev(normSev(r.severity_level));
    } else if (field === 'tipoos') {
      setEditing({ cod, field: 'tipoos' });
      const flags = {};
      TIPO_OS_FIELDS.forEach((f) => {
        flags[f.db] = normOsFlag(r[f.db]);
      });
      setDraftTipoFlags(flags);
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraftBoard('');
    setDraftColor('');
    setDraftTags([]);
    setTagInput('');
    setDraftSev('leve');
    setDraftTipoFlags({});
  };

  const saveEdit = async (r) => {
    if (!editing || editing.cod !== String(r.TB01048_CODIGO).trim()) return;
    const cod = editing.cod;
    const body = {};
    if (editing.field === 'board') body.codAlert = draftBoard;
    if (editing.field === 'color') body.colorBg = draftColor.trim() === '' ? '' : draftColor.trim();
    if (editing.field === 'classif') body.classification = joinTags(draftTags);
    if (editing.field === 'sev') body.severityLevel = draftSev;
    if (editing.field === 'tipoos') {
      TIPO_OS_FIELDS.forEach((f) => {
        body[f.api] = normOsFlag(draftTipoFlags?.[f.db]);
      });
    }

    setSaving({ cod, field: editing.field });
    try {
      const j = await apiPatch(cod, body);
      if (j.success) {
        if (body.codAlert !== undefined) patchRowLocal(cod, { cod_alert: body.codAlert });
        if (body.colorBg !== undefined) patchRowLocal(cod, { color_bg: body.colorBg || null });
        if (body.classification !== undefined) patchRowLocal(cod, { classification: body.classification });
        if (body.severityLevel !== undefined) patchRowLocal(cod, { severity_level: body.severityLevel });
        if (editing.field === 'tipoos') {
          const partial = {};
          TIPO_OS_FIELDS.forEach((f) => {
            partial[f.db] = normOsFlag(draftTipoFlags?.[f.db]);
          });
          patchRowLocal(cod, partial);
        }
        cancelEdit();
      } else {
        setFeedback({ type: 'err', cod, msg: j.message || 'Erro ao salvar' });
        setTimeout(() => setFeedback(null), 4000);
      }
    } catch {
      setFeedback({ type: 'err', cod, msg: 'Erro de conexão' });
      setTimeout(() => setFeedback(null), 4000);
    } finally {
      setSaving(null);
    }
  };

  const onSeverityChange = async (r, value) => {
    const cod = String(r.TB01048_CODIGO).trim();
    patchRowLocal(cod, { severity_level: value });
    setSaving({ cod, field: 'sev' });
    try {
      const j = await apiPatch(cod, { severityLevel: value });
      if (!j.success) {
        setFeedback({ type: 'err', cod, msg: j.message || 'Erro' });
        load();
        setTimeout(() => setFeedback(null), 4000);
      } else {
      }
    } catch {
      load();
    } finally {
      setSaving(null);
    }
  };

  const onTipoOsToggle = async (r, f, checked) => {
    const cod = String(r.TB01048_CODIGO).trim();
    const val = checked ? 1 : 0;
    patchRowLocal(cod, { [f.db]: val });
    setSaving({ cod, field: 'tipoos' });
    try {
      const j = await apiPatch(cod, { [f.api]: val });
      if (!j.success) {
        setFeedback({ type: 'err', cod, msg: j.message || 'Erro' });
        load();
        setTimeout(() => setFeedback(null), 4000);
      }
    } catch {
      load();
    } finally {
      setSaving(null);
    }
  };

  const addTagFromInput = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (draftTags.includes(t)) {
      setTagInput('');
      return;
    }
    setDraftTags((prev) => [...prev, t]);
    setTagInput('');
  };

  const persistClassification = async (row, tags) => {
    const cod = String(row.TB01048_CODIGO).trim();
    const classification = joinTags(tags);
    setDraftTags(tags);
    patchRowLocal(cod, { classification });
    setSaving({ cod, field: 'classif' });
    try {
      const j = await apiPatch(cod, { classification });
      if (!j.success) {
        setFeedback({ type: 'err', cod, msg: j.message || 'Erro ao salvar' });
        load();
        setTimeout(() => setFeedback(null), 4000);
      }
    } catch {
      setFeedback({ type: 'err', cod, msg: 'Erro de conexão' });
      load();
      setTimeout(() => setFeedback(null), 4000);
    } finally {
      setSaving(null);
    }
  };

  const addTagFromInputAndSave = (row) => {
    const t = tagInput.trim();
    if (!t) return;
    if (draftTags.includes(t)) {
      setTagInput('');
      return;
    }
    const next = [...draftTags, t];
    setTagInput('');
    persistClassification(row, next);
  };

  const removeTagAndSave = (row, tag) => {
    const next = draftTags.filter((x) => x !== tag);
    persistClassification(row, next);
  };

  const saveModalSuggestion = async (html) => {
    if (!modalRow) return;
    const cod = String(modalRow.TB01048_CODIGO).trim();
    setModalSaving(true);
    try {
      const j = await apiPatch(cod, { autoSolutionSuggestions: html });
      if (j.success) {
        patchRowLocal(cod, { auto_solution_suggestions: html });
        setModalRow(null);
      } else {
        setFeedback({ type: 'err', cod, msg: j.message || 'Erro ao salvar' });
        setTimeout(() => setFeedback(null), 4000);
      }
    } catch {
      setFeedback({ type: 'err', cod, msg: 'Erro de conexão' });
      setTimeout(() => setFeedback(null), 4000);
    } finally {
      setModalSaving(false);
    }
  };

  const displayRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = [...rows];
    if (q) {
      list = list.filter((row) => {
        const haystack = [
          row.TB01048_CODIGO,
          row.TB01048_NOME,
          row.cod_alert,
          row.classification,
          row.severity_level,
        ]
          .map((v) => String(v ?? '').toLowerCase())
          .join(' ');
        return haystack.includes(q);
      });
    }
    if (nativeMode) {
      list = list.filter((row) => (statusTab === 'inativos' ? row.is_active === false : row.is_active !== false));
    }
    const dir = sortDir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      const va = getDefeitoSortValue(a, sortBy).toLowerCase();
      const vb = getDefeitoSortValue(b, sortBy).toLowerCase();
      const na = Number(va);
      const nb = Number(vb);
      if (va !== '' && vb !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * dir;
      return va.localeCompare(vb, 'pt-BR', { sensitivity: 'base', numeric: true }) * dir;
    });
    return list;
  }, [rows, searchQuery, sortBy, sortDir, nativeMode, statusTab]);

  const tableApi = useMemo(
    () => ({
      rowEnabled,
      isDefeitoColVis,
      editing,
      draftBoard,
      setDraftBoard,
      draftColor,
      setDraftColor,
      draftTags,
      setDraftTags,
      tagInput,
      setTagInput,
      draftSev,
      setDraftSev,
      draftTipoFlags,
      setDraftTipoFlags,
      saving,
      sevOpenCod,
      setSevOpenCod,
      tipoOsOpenCod,
      setTipoOsOpenCod,
      colorPaletteOpen,
      setColorPaletteOpen,
      colorPaletteOpenCod,
      colorEditAnchorRef,
      closeColorPalette,
      closeColorPalettePicker,
      openColorPalette,
      saveColorPick,
      startEdit,
      cancelEdit,
      saveEdit,
      addTagFromInput,
      addTagFromInputAndSave,
      removeTagAndSave,
      onSeverityChange,
      onTipoOsToggle,
      setModalRow,
      setEquipModalRow,
      desabilitarOpenId,
      setDesabilitarOpenId,
      desabilitar,
      disablingId,
      enablingId,
      habilitar,
      feedback,
      nativeMode,
      openEditService,
    }),
    [
      rowEnabled,
      isDefeitoColVis,
      editing,
      draftBoard,
      draftColor,
      draftTags,
      tagInput,
      draftSev,
      draftTipoFlags,
      saving,
      sevOpenCod,
      tipoOsOpenCod,
      colorPaletteOpen,
      colorPaletteOpenCod,
      closeColorPalette,
      closeColorPalettePicker,
      openColorPalette,
      saveColorPick,
      startEdit,
      cancelEdit,
      saveEdit,
      addTagFromInput,
      addTagFromInputAndSave,
      removeTagAndSave,
      onSeverityChange,
      onTipoOsToggle,
      desabilitarOpenId,
      disablingId,
      enablingId,
      desabilitar,
      habilitar,
      feedback,
      nativeMode,
      openEditService,
    ]
  );

  return (
    <div className="pcs2-page defeitos-verve-page">
      <Pcs2Section
        className="defeitos-verve-section-table"
        icon={Bug}
        title="Configuração de serviços"
        desc="Visualize serviços do ERP e os configure nesta área"
      >
        <Pcs2Subblock
          title="Tabela de serviços"
          desc="Habilite e trabalhe com serviços alinhados ao universo Verve."
          action={
            <DefeitosVerveToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={setSortBy}
              onSortDirChange={setSortDir}
              isDefeitoColVis={isDefeitoColVis}
              onToggleCol={(k) => {
                setColDefeitoVisibility((prev) => ({ ...prev, [k]: prev[k] === false ? true : false }));
              }}
              onRestoreCols={() => setColDefeitoVisibility(fullDefeitosVisibilityState())}
            >
              {nativeMode ? (
                <span className="dv2-native-actions">
                  <div className="go-dev-status-tabs" role="tablist" aria-label="Filtrar serviços por situação">
                    {[
                      ['ativos', 'Ativos'],
                      ['inativos', 'Inativos'],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={statusTab === key}
                        className={`go-dev-status-tab${statusTab === key ? ' is-active' : ''}`}
                        onClick={() => setStatusTab(key)}
                      >
                        <span>{label}</span>
                        <span className="go-dev-status-tab-count">
                          {rows.filter((row) => (key === 'inativos' ? row.is_active === false : row.is_active !== false)).length}
                        </span>
                      </button>
                    ))}
                  </div>
                  <GoHoverTooltip text={serviceForm ? '' : 'Novo serviço'} align="center" zIndex={12000}>
                    <button
                      type="button"
                      className="equipe2-add-btn equipe2-add-btn--attention"
                      aria-label="Novo serviço"
                      onClick={() => setServiceForm({ mode: 'create', codigo: null })}
                    >
                      <span className="equipe2-add-btn-plus">+</span>
                    </button>
                  </GoHoverTooltip>
                </span>
              ) : null}
            </DefeitosVerveToolbar>
          }
        >
          {loading ? (
            <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando serviços" />
          ) : (
            <div className="defeitos-verve-table-wrap">
              <DefeitosVerveTableV2 rows={displayRows} totalRows={rows.length} api={tableApi} />
            </div>
          )}

        </Pcs2Subblock>

        <Pcs2Subblock hideHead>
          <div className="pcs2-card pcs2-card--flat">
            <Pcs2SettingRow
              title="Serviços, tipos de OS e equipamentos"
              desc="Associe serviços habilitados a tipos de ordem de serviço e modelos de equipamentos para abertura da OS."
            >
              <Pcs2Switch
                checked={equipAssocEnabled}
                disabled={!equipAssocLoaded || equipAssocSaving}
                onChange={(e) => onEquipAssocToggle(e.target.checked)}
                ariaLabel="Associar defeitos a equipamentos e tipos de OS na abertura"
              />
            </Pcs2SettingRow>
          </div>
          {feedback && feedback.cod === '__equip_assoc__' && feedback.type === 'err' ? (
            <div className="condicoes-msg-erro defeitos-vv-flag-err">{feedback.msg}</div>
          ) : null}
        </Pcs2Subblock>
      </Pcs2Section>

      <RichTextModal
        open={!!modalRow}
        initialHtml={modalRow?.auto_solution_suggestions || ''}
        title={modalRow?.TB01048_NOME || ''}
        onClose={() => !modalSaving && setModalRow(null)}
        onSave={saveModalSuggestion}
        saving={modalSaving}
      />

      <DefeitosEquipCompatModal
        open={!!equipModalRow}
        row={equipModalRow}
        onClose={() => setEquipModalRow(null)}
        onLinkedCountChange={(cod, n) => patchRowLocal(cod, { equip_count: n })}
      />

      <DefeitosVerveNativeServiceModal
        open={!!serviceForm}
        mode={serviceForm?.mode}
        codigo={serviceForm?.codigo}
        row={rows.find((item) => String(item.TB01048_CODIGO).trim() === String(serviceForm?.codigo || '').trim()) || null}
        nativeMode={nativeMode}
        baseUrl={API_BASE_URL}
        onClose={() => setServiceForm(null)}
        onSaved={load}
        onLinkedCountChange={(cod, n) => patchRowLocal(cod, { equip_count: n })}
      />
    </div>
  );
}
